import { useQueryClient } from '@tanstack/react-query';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { NotificationType } from '../../types/notification.types';
import { useAppQuery } from '../use-app-query.hook';

export const useUnreadNotificationCount = ({
  companyId,
  type,
  enabled,
  stakeholderInAppNotifications,
  aminInAppNotifications,
}: {
  aminInAppNotifications?: boolean;
  companyId: string;
  enabled?: boolean;
  stakeholderInAppNotifications?: boolean;
  type?: NotificationType;
}) => {
  const queryClient = useQueryClient();
  const {
    data: count,
    dataUpdatedAt,
    isLoading,
  } = useAppQuery({
    queryKey: [
      QueryKey.GET_UNREAD_NOTIFICATION_COUNT,
      companyId,
      type,
      stakeholderInAppNotifications,
      aminInAppNotifications,
    ],
    queryFn: () =>
      ApiService.get<number>(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [companyId, 'notifications', 'unread-count'],
        },
        { queryParams: { type } },
      ),
    enabled: !!companyId && enabled,
    staleTime: 25_000,
    // Notifications can disappear server-side without a websocket push (an event delete cascades
    // its request notifications away), so the badge has to re-read on focus. staleTime bounds it.
    refetchOnWindowFocus: true,
  });

  const invalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_UNREAD_NOTIFICATION_COUNT] });
  };

  return {
    unread: {
      count,
      type,
    },
    // Consumers mirror `count` into local state and mutate it optimistically; re-syncing on value
    // change alone leaves the mirror drifted whenever a refetch returns the same number it last
    // returned. `dataUpdatedAt` marks every successful read, so it can drive the re-sync instead.
    dataUpdatedAt,
    isLoading,
    invalidateQuery,
  };
};
